import { useCallback } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useForm } from "react-hook-form";
import Button from "../../../components/Buttons/Button";
import TextField from "../../../components/Form/TextField";
import { useTranslationApp } from "../../../lib/i18next";
import {
  BUTTON_CANCEL,
  CAMPAIGN_APPROVAL_SPECIFIC_COMMENT_BUTTON,
  FIELD_REQUIRED,
} from "../../../i18n/keysTranslations";

const CommentForm = ({
  title,
  description = "",
  placeholder,
  submitLabel,
  onSubmit,
  onCancel,
  disabled = false,
  loading = false,
  compact = false,
  inline = false,
}) => {
  const { t } = useTranslationApp();
  const { control, handleSubmit, reset, formState } = useForm({
    defaultValues: {
      body: "",
    },
  });

  const onHandleSubmit = handleSubmit(async ({ body }) => {
    const normalizedBody = body?.trim?.() || "";
    const response = await onSubmit?.({
      body: normalizedBody,
    });

    if (response?.ok) {
      reset({
        body: "",
      });
      onCancel?.();
    }
  });

  const onKeyDown = useCallback(
    (event) => {
      if (
        (event.metaKey || event.ctrlKey) &&
        event.key === "Enter" &&
        !disabled &&
        !loading &&
        !formState.isSubmitting
      ) {
        event.preventDefault();
        onHandleSubmit();
      }
    },
    [disabled, formState.isSubmitting, loading, onHandleSubmit]
  );

  if (inline) {
    return (
      <Box
        component="form"
        onSubmit={onHandleSubmit}
        sx={{
          width: "100%",
          mt: 1,
        }}
      >
        <TextField
          control={control}
          name="body"
          label=""
          placeholder={t(placeholder)}
          fullWidth={true}
          multiline={true}
          minRows={3}
          variant="filled"
          disabled={disabled || loading || formState.isSubmitting}
          onKeyDown={onKeyDown}
          rules={{
            required: {
              value: true,
              message: t(FIELD_REQUIRED),
            },
            validate: (value) => {
              return value?.trim?.() ? true : t(FIELD_REQUIRED);
            },
          }}
        />

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
            mt: 1,
          }}
        >
          {onCancel ? (
            <Button
              type="button"
              color="secondary"
              onClick={onCancel}
              disabled={loading || formState.isSubmitting}
            >
              {t(BUTTON_CANCEL)}
            </Button>
          ) : null}

          <Button
            type="submit"
            disabled={disabled || loading}
            loading={loading || formState.isSubmitting}
          >
            {t(submitLabel || CAMPAIGN_APPROVAL_SPECIFIC_COMMENT_BUTTON)}
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      component="form"
      onSubmit={onHandleSubmit}
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: compact ? 1 : 1.5,
      }}
    >
      {title ? (
        <Typography
          variant={compact ? "subtitle2" : "subtitle1"}
          fontWeight={600}
        >
          {t(title)}
        </Typography>
      ) : null}

      {description ? (
        <Typography variant="body2" color="text.secondary">
          {t(description)}
        </Typography>
      ) : null}

      <TextField
        control={control}
        name="body"
        label=""
        placeholder={t(placeholder)}
        fullWidth={true}
        multiline={true}
        minRows={compact ? 3 : 4}
        variant="filled"
        disabled={disabled || loading || formState.isSubmitting}
        rules={{
          required: {
            value: true,
            message: t(FIELD_REQUIRED),
          },
          validate: (value) => {
            return value?.trim?.() ? true : t(FIELD_REQUIRED);
          },
        }}
      />

      <Box
        sx={{
          display: "flex",
          gap: 1,
          justifyContent: "flex-end",
          flexWrap: "wrap",
        }}
      >
        {onCancel ? (
          <Button
            type="button"
            color="secondary"
            onClick={onCancel}
            disabled={loading || formState.isSubmitting}
          >
            {t(BUTTON_CANCEL)}
          </Button>
        ) : null}

        <Button
          type="submit"
          disabled={disabled || loading}
          loading={loading || formState.isSubmitting}
        >
          {t(submitLabel)}
        </Button>
      </Box>
    </Box>
  );
};

export default CommentForm;
